/* eslint-disable sonarjs/no-duplicate-string */
import Tokens from 'styles/tokens';

const LITE_FEATURES = [
  'Automatic Video Recording',
  'Unlimited Transcription',
  'Advanced AI-generated Notes',
  'Group & Round-Robin Scheduler',
  'CRM & Dialer Integration'
];
const PLUS_FEATURES = [
  'Manual and AI Scorecards',
  'Talk Pattern & Topic Intelligence',
  'Smart Tracker Intelligence',
  'Engagement & ROI Intelligence'
];
const PREMIUM_FEATURES = [
  'Automatic CRM field updates',
  'Custom Deal Boards, Risk Scores',
  'Roll-up Forecasting & Reports',
  'AI Win-Loss Analysis (Add-on)'
];
const API_ACCESS_FEATURES = [
  'API access',
  'Zapier integration',
  'External webhooks'
];
const STARTUP_FEATURES = [
  'Unlimited Free View-only Seats',
  'Unlimited 1:1 Scheduling',
  'Automatic Video Recording',
  'Unlimited Real-time Transcription',
  'Unlimited AI Summary Notes',
  'Ask Avoma for a Meeting',
  'AI Email Follow-ups',
  'Auto Save Notes to CRM Records',
  'Dialer Integration'
];
const ORGANIZATION_FEATURES = [
  'Custom AI Topics & Templates',
  'Group and Round-robin Scheduling',
  'Limited Conversation Intelligence',
  'Smart Playlist & AI Automations',
  'Translation & Notes Regeneration',
  'Custom AI Email Templates',
  'Enforce Org-level Policies',
  'API Integration & Webhooks'
];
const ENTERPRISE_V2_FEATURES = [
  'Ask Avoma across All Meetings',
  'Designated Success Manager',
  'Concierge Onboarding & Training',
  'Quarterly Business Reviews',
  'Unlimited Usage Intelligence',
  'Team-Specific Access Controls',
  'Mutually Signed DPAs',
  'Single Sign-On (SAML/OIDC)',
  'Data Retention Policies',
  'HIPAA Compliance'
];

const renderEverythingInTier = (tierName, key) => (
  <span key={key}>
    Everything in{' '}
    <strong style={{ color: Tokens.colors.gunmetal }}>{tierName}</strong>
  </span>
);

export const planFeatureList = {
  'basic-monthly': [
    'Recording & Transcription',
    'Limited AI-generated Notes',
    'Collaborative Note-taking',
    'Agenda Preparation',
    'Unlimited 1:1 Scheduler'
  ],
  'lite-monthly': [...LITE_FEATURES],
  'lite-yearly': [...LITE_FEATURES],

  'plus-monthly': [
    renderEverythingInTier('AI Meeting Assistant', 'plus-monthly'),
    ...PLUS_FEATURES
  ],
  'plus-yearly': [
    renderEverythingInTier('AI Meeting Assistant', 'plus-yearly'),
    ...PLUS_FEATURES
  ],

  'premium-monthly': [
    renderEverythingInTier('Conversation Intelligence', 'premium-monthly'),
    ...PREMIUM_FEATURES
  ],
  'premium-yearly': [
    renderEverythingInTier('Conversation Intelligence', 'premium-yearly'),
    ...PREMIUM_FEATURES
  ],
  'startup-monthly': [...STARTUP_FEATURES],
  'startup-half-yearly': [...STARTUP_FEATURES],
  'startup-yearly': [...STARTUP_FEATURES],
  'startup-api-bundle-monthly': [
    renderEverythingInTier('Startup', 'startup-api-bundle-monthly'),
    ...API_ACCESS_FEATURES
  ],
  'startup-api-bundle-half-yearly': [
    renderEverythingInTier('Startup', 'startup-api-bundle-half-yearly'),
    ...API_ACCESS_FEATURES
  ],
  'startup-api-bundle-yearly': [
    renderEverythingInTier('Startup', 'startup-api-bundle-yearly'),
    ...API_ACCESS_FEATURES
  ],
  'organization-monthly': [...ORGANIZATION_FEATURES],
  'organization-half-yearly': [...ORGANIZATION_FEATURES],
  'organization-yearly': [...ORGANIZATION_FEATURES],
  'enterprise-v2-monthly': [...ENTERPRISE_V2_FEATURES],
  'enterprise-v2-half-yearly': [...ENTERPRISE_V2_FEATURES],
  'enterprise-v2-yearly': [...ENTERPRISE_V2_FEATURES]
};

export const PLAN_SEAT_LIMIT_FALLBACKS = {
  startup: {
    type: 'max',
    value: 25
  },
  'startup-api-bundle': {
    type: 'max',
    value: 25
  },
  organization: {
    type: 'max',
    value: 100
  },
  'enterprise-v2': {
    type: 'min',
    value: 10
  }
};

export const ADDON_FEATURE_LIST = {
  'lead-router-addon': [
    '1:1 Scheduling Links',
    'Customize Event Titles',
    'Embed Scheduler to your Website',
    'Save contacts to CRM',
    'Group Scheduling',
    'Round Robin Scheduling',
    'Real-time Lead Qualification and Routing',
    'Round Robin Scheduling based on Custom Rules and CRM fields',
    'Lead Routing based CRM Contact and Account Owner',
    'Lead Routing based on CRM Custom fields',
    'Form Field Based Router',
    'SDR to AE Handoff Router'
  ],
  'activity-capture-addon': [
    'Auto-sync emails, meetings, and contacts to CRM',
    'Auto-map activities to the right CRM records',
    'CRM field updates',
    'Chrome extension to see and adjust mapping',
    'Deal and Account-based Ask Avoma'
  ],
  'ci-addon': [
    'AI Coaching Recommendations',
    'Auto Call Scoring with AI',
    'Custom AI Scorecards',
    'SPICED, Sandler, MEDDICC, etc. AI Scorecards',
    'Smart Playlists – Rule Based Auto Curation',
    'Real-time Answer Assistant',
    'Member Performance Trend Dashboard',
    'Scorecard Performance Trend Dashboard',
    'Interaction & Talk-Pattern Skills',
    'Standard Topic Intelligence',
    'Custom Keyword Trackers',
    'Custom Smart Topic Trackers'
  ],
  'ri-addon': [
    '"Ask Avoma" across Accounts',
    '"Ask Avoma" across Deals',
    'Dealboard for Pipeline Review',
    '2-way CRM Field Updates',
    'AI-powered Deal Risk Alerts',
    'AI Deal Health Score',
    'AI Sales Methodology Tracker',
    'Roll-up & Aggregated Forecasting',
    'AI Win-Loss Deal Analysis',
    'Pipeline and Forecasting Reports',
    'Native CRM Integration',
    'Dialer Integration'
  ],
  'task-management-addon': [
    'Auto-create Tasks from Meetings',
    'AI-powered Task Prioritization',
    'AI-powered Task Assignment',
    'Auto-draft Follow-up Emails'
  ],
  'sso-addon': [
    'SAML 2.0 Single Sign-On',
    'OpenID Connect (OIDC) Support',
    'Mobile App SSO Support'
  ]
};

export const PLAN_SUBTITLES = {
  'enterprise-v2': (
    <strong>
      Everything in <span className='text-yellow-600'>Organization</span>, plus:
    </strong>
  ),
  startup: (
    <strong>
      <span className='text-avoma-red'>Unlimited</span> AI Meeting Assistant:
    </strong>
  ),
  organization: (
    <strong>
      Everything in <span className='text-green-600'>Startup</span>, plus:
    </strong>
  )
};

export const PLAN_NAMES = {
  basic: 'Basic',
  lite: 'AI Meeting Assistant',
  plus: 'Conversation Intelligence',
  trial: 'Unrestricted Trial',
  premium: 'Revenue Intelligence',
  enterprise: 'Enterprise',
  'enterprise-v2': 'Enterprise',
  startup: 'Startup',
  'startup-api-bundle': 'Startup with API Access',
  team: 'Startup',
  organization: 'Organization'
};

export const BILLING_ADDONS = {
  aiWinLossAnalysis: 'AI Win-Loss Analysis',
  'lead-router-addon': 'Lead Router',
  'ci-addon': 'Conversation Intelligence',
  'ri-addon': 'Revenue Intelligence',
  'activity-capture-addon': 'Activity Capture',
  'task-management-addon': 'Task Management',
  'sso-addon': 'Single Sign-On'
};
